<template>
  <article class="inventory-card" :class="{'inventory-card--selected': selected}">
    <div class="inventory-card__media">
      <img
        v-if="imageUrl && !imageFailed"
        :src="imageUrl"
        :alt="vehicleTitle"
        class="inventory-card__image"
        @error="imageFailed = true"
      >
      <div v-else class="inventory-card__placeholder" :aria-label="$t('Inventory.no_photo')">
        <i class="fa fa-car" aria-hidden="true" />
        <span>{{ $t('Inventory.no_photo') }}</span>
      </div>

      <label v-if="canWrite && canSelect" class="inventory-card__selector">
        <input
          type="checkbox"
          :checked="selected"
          :aria-label="`${$t('Inventory.select_vehicle')} ${orderId}`"
          @change="$emit('toggle-selection', orderId)"
        >
        <span>{{ $t('Inventory.select') }}</span>
      </label>

      <span class="inventory-card__state" :class="stateClass">
        {{ hasLeft ? $t('Inventory.state_left') : $t('Inventory.state_inside') }}
      </span>

      <label
        v-if="canWrite"
        class="inventory-card__lot inventory-card__lot--editable"
        :class="{'inventory-card__lot--unassigned': !inventoryLot}"
      >
        <i class="fa fa-map-marker" aria-hidden="true" />
        <span class="sr-only">{{ $t('Inventory.change_lot', {orderId: orderId}) }}</span>
        <select
          :value="inventoryLot"
          :disabled="updatingLot"
          :aria-label="$t('Inventory.change_lot', {orderId: orderId})"
          @change="changeLot"
        >
          <option value="" disabled>{{ $t('Inventory.choose_lot') }}</option>
          <option v-for="lot in inventoryLots" :key="lot" :value="lot">{{ $t('Inventory.lot') }} {{ lot }}</option>
        </select>
        <i v-if="updatingLot" class="fa fa-spinner fa-spin" aria-hidden="true" />
        <i v-else class="fa fa-chevron-down inventory-card__lot-chevron" aria-hidden="true" />
      </label>
      <span v-else class="inventory-card__lot" :class="{'inventory-card__lot--unassigned': !inventoryLot}">
        <i class="fa fa-map-marker" aria-hidden="true" />
        <template v-if="inventoryLot">{{ $t('Inventory.lot') }} {{ inventoryLot }}</template>
        <template v-else>{{ $t('Inventory.unassigned_lot') }}</template>
      </span>

      <span class="inventory-card__term" :class="{'inventory-card__term--unassigned': !inventoryTerm}">
        <i class="fa fa-clock-o" aria-hidden="true" />
        {{ inventoryTermLabel }}
      </span>
    </div>

    <div class="inventory-card__content">
      <div class="inventory-card__heading">
        <div>
          <router-link
            class="inventory-card__number"
            :to="{name: 'OrderInfo', query: {orderId: orderId}}"
          >
            {{ orderId }}
          </router-link>
          <h2>{{ vehicleTitle }}</h2>
        </div>
        <status-badge
          v-if="order.status"
          :status="order.status"
          size="sm"
        />
      </div>

      <div class="inventory-card__vin">
        <span>{{ $t('Inventory.vin') }}</span>
        <strong>{{ order.vin || $t('Inventory.not_provided') }}</strong>
      </div>

      <dl class="inventory-card__details">
        <div>
          <dt><i class="fa fa-building-o" aria-hidden="true" /> {{ $t('Inventory.billing_name') }}</dt>
          <dd>{{ $realName(order, 'payer') || '—' }}</dd>
        </div>
        <div>
          <dt><i class="fa fa-link" aria-hidden="true" /> {{ $t('Inventory.source_reference') }}</dt>
          <dd>{{ sourceReference }}</dd>
        </div>
        <div>
          <dt><i class="fa fa-sign-in" aria-hidden="true" /> {{ $t('Inventory.date_entered') }}</dt>
          <dd>{{ formattedDateEntered }}</dd>
        </div>
        <div>
          <dt><i class="fa fa-sign-out" aria-hidden="true" /> {{ $t('Inventory.date_left') }}</dt>
          <dd>{{ formattedDateLeft }}</dd>
        </div>
      </dl>

      <div class="inventory-card__actions">
        <button
          v-if="canWrite && canConfirm"
          type="button"
          class="btn btn-kne-green"
          :disabled="confirming"
          @click="$emit('confirm-arrival', order)"
        >
          <i :class="['fa', confirming ? 'fa-spinner fa-spin' : 'fa-check']" aria-hidden="true" />
          {{ confirming ? $t('Inventory.confirming_arrival') : $t('Inventory.confirm_arrival') }}
        </button>
        <router-link
          class="btn btn-light inventory-card__details-link"
          :to="{name: 'OrderInfo', query: {orderId: orderId}}"
        >
          {{ $t('Inventory.view_order') }}
        </router-link>
        <button
          v-if="canWrite && canBill"
          type="button"
          class="btn btn-kne-orange"
          @click="$emit('bill', order)"
        >
          <i class="fa fa-check-circle mr-1" aria-hidden="true" />
          {{ $t('Inventory.billed_done') }}
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import StatusBadge from '../Other-components/StatusBadge.vue'
import { formatTorontoDateTime } from './inventoryDateTime'
import {
  INVENTORY_TERM,
  canConfirmInventoryArrival,
  isInventoryTerm,
  normalizeInventoryLotName
} from '../../../../inventory/inventorySelectors'

export default {
  name: 'InventoryCard',

  components: {
    StatusBadge
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    imageUrl: {
      type: String,
      default: ''
    },
    selected: {
      type: Boolean,
      default: false
    },
    canWrite: {
      type: Boolean,
      default: false
    },
    confirming: {
      type: Boolean,
      default: false
    },
    updatingLot: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      imageFailed: false
    }
  },

  computed: {
    inventoryLots () {
      return this.$store.getters.inventoryLotNames
    },
    orderId () {
      return this.order['.key'] || this.order.orderNumber || this.order.key || ''
    },
    vehicleTitle () {
      const description = [this.order.year, this.order.make, this.order.model, this.order.trim]
        .filter(Boolean)
        .join(' ')
      return description || this.$t('Inventory.vehicle_pending')
    },
    hasLeft () {
      return Boolean(this.order.dateLeft)
    },
    inventoryLot () {
      const lot = normalizeInventoryLotName(this.order.inventoryLot)
      return this.inventoryLots.includes(lot) ? lot : ''
    },
    inventoryTerm () {
      const term = String(this.order.inventoryTerm || '').trim()
      return isInventoryTerm(term) ? term : ''
    },
    inventoryTermLabel () {
      if (this.inventoryTerm === INVENTORY_TERM.SHORT_TERM) return this.$t('Inventory.short_term')
      if (this.inventoryTerm === INVENTORY_TERM.LONG_TERM) return this.$t('Inventory.long_term')
      return this.$t('Inventory.unassigned_term')
    },
    canSelect () {
      return Boolean(this.order.dateEntered) && !this.order.dateLeft
    },
    canBill () {
      return Boolean(this.order.dateEntered) && Boolean(this.order.dateLeft) && !this.order.inventoryBilledAt
    },
    canConfirm () {
      return canConfirmInventoryArrival(this.order)
    },
    stateClass () {
      return this.hasLeft ? 'inventory-card__state--left' : 'inventory-card__state--inside'
    },
    sourceReference () {
      const source = this.order.source || this.$t('Inventory.title')
      const reference = this.order.numRef || this.order.sourceOrderId
      return reference ? `${source} · ${reference}` : source
    },
    formattedDateEntered () {
      return formatTorontoDateTime(this.order.dateEntered)
    },
    formattedDateLeft () {
      return formatTorontoDateTime(this.order.dateLeft)
    }
  },

  watch: {
    imageUrl () {
      this.imageFailed = false
    }
  },

  methods: {
    changeLot (event) {
      this.$emit('change-lot', {
        order: this.order,
        inventoryLot: event.target.value
      })
    }
  }
}
</script>

<style scoped>
.inventory-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid #e6e9ee;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(27, 39, 51, .08);
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.inventory-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(27, 39, 51, .12);
}

.inventory-card--selected {
  border-color: #f4a340;
  box-shadow: 0 0 0 3px rgba(244, 163, 64, .17), 0 12px 30px rgba(27, 39, 51, .12);
}

.inventory-card__media {
  position: relative;
  height: 220px;
  overflow: hidden;
  background: #edf1f5;
}

.inventory-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.inventory-card__placeholder {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #8b96a5;
  background: linear-gradient(135deg, #f3f5f7, #e3e8ed);
}

.inventory-card__placeholder i {
  font-size: 3.5rem;
  opacity: .6;
}

.inventory-card__placeholder span {
  font-size: .82rem;
  font-weight: 600;
  letter-spacing: .03em;
  text-transform: uppercase;
}

.inventory-card__selector,
.inventory-card__state {
  position: absolute;
  top: 14px;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .15);
  font-size: .78rem;
  font-weight: 700;
}

.inventory-card__selector {
  left: 14px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  margin: 0;
  cursor: pointer;
  color: #243342;
  background: rgba(255, 255, 255, .95);
}

.inventory-card__selector input {
  width: 16px;
  height: 16px;
}

.inventory-card__state {
  right: 14px;
  padding: 8px 11px;
}

.inventory-card__state--inside {
  color: #17663c;
  background: #ddf7e8;
}

.inventory-card__state--left {
  color: #794f0a;
  background: #fff0c8;
}

.inventory-card__lot {
  position: absolute;
  right: 14px;
  bottom: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 11px;
  border-radius: 9px;
  color: #243342;
  background: rgba(255, 255, 255, .95);
  box-shadow: 0 4px 12px rgba(0, 0, 0, .15);
  font-size: .78rem;
  font-weight: 800;
}

.inventory-card__lot i {
  color: #d97918;
}

.inventory-card__lot--unassigned {
  color: #6d7883;
  font-weight: 700;
}

.inventory-card__lot--editable {
  margin: 0;
  cursor: pointer;
}

.inventory-card__lot--editable select {
  max-width: 92px;
  padding: 0;
  border: 0;
  outline: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
  font: inherit;
  appearance: none;
}

.inventory-card__lot--editable select:disabled {
  cursor: wait;
  opacity: .7;
}

.inventory-card__lot-chevron {
  color: #6d7883 !important;
  font-size: .65rem;
}

.inventory-card__term {
  position: absolute;
  bottom: 14px;
  left: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 11px;
  border-radius: 9px;
  color: #28566e;
  background: rgba(236, 248, 253, .96);
  box-shadow: 0 4px 12px rgba(0, 0, 0, .15);
  font-size: .78rem;
  font-weight: 800;
}

.inventory-card__term i {
  color: #3e718e;
}

.inventory-card__term--unassigned {
  color: #6d7883;
  background: rgba(255, 255, 255, .95);
  font-weight: 700;
}

.inventory-card__content {
  padding: 20px;
}

.inventory-card__heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.inventory-card__heading h2 {
  margin: 3px 0 0;
  color: #25313d;
  font-size: 1.2rem;
  line-height: 1.3;
}

.inventory-card__number {
  color: #d97918;
  font-size: .83rem;
  font-weight: 800;
  letter-spacing: .05em;
}

.inventory-card__vin {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  margin: 16px 0;
  border-radius: 10px;
  background: #f5f7f9;
}

.inventory-card__vin span {
  color: #7c8794;
  font-size: .7rem;
  font-weight: 700;
  letter-spacing: .08em;
}

.inventory-card__vin strong {
  overflow-wrap: anywhere;
  color: #25313d;
  font-family: monospace;
  font-size: .94rem;
  letter-spacing: .04em;
}

.inventory-card__details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
}

.inventory-card__details div {
  min-width: 0;
}

.inventory-card__details dt {
  margin-bottom: 3px;
  color: #818c98;
  font-size: .73rem;
  font-weight: 600;
}

.inventory-card__details dd {
  overflow: hidden;
  margin: 0;
  color: #374453;
  font-size: .86rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-card__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 9px;
  padding-top: 18px;
  margin-top: 18px;
  border-top: 1px solid #edf0f3;
}

.inventory-card__details-link {
  color: #4e5d6d;
  background: #eef1f4;
}

@media (max-width: 430px) {
  .inventory-card__media {
    height: 190px;
  }

  .inventory-card__details {
    grid-template-columns: 1fr;
  }

  .inventory-card__actions {
    flex-direction: column;
  }

  .inventory-card__actions .btn {
    width: 100%;
  }
}
</style>
